import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import {
  pageStyle,
  headingStyle,
  mutedTextStyle,
  errorBoxStyle,
  retryBtnStyle,
  backBtnStyle,
  badgeStyle,
  bracketWrapStyle,
  bracketRoundColStyle,
  bracketRoundTitleStyle,
  bracketMatchCardStyle,
  bracketTeamRowStyle,
  bracketTeamNameStyle,
  bracketScoreStyle,
  bracketWinnerStyle,
  bracketByeStyle,
  bracketSeedBtnStyle,
  bracketSeedFormStyle,
  bracketSeedRowStyle,
  bracketSeedLabelStyle,
  bracketSeedInputStyle,
  bracketSeedSelectStyle,
  bracketStatusBadgeStyle,
} from '../App.styles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BracketMatch {
  id: string;
  position: number;
  teamAId: string | null;
  teamBId: string | null;
  teamAName: string | null;
  teamBName: string | null;
  scoreA: number | null;
  scoreB: number | null;
  winnerId: string | null;
  winnerName: string | null;
}

interface BracketRound {
  id: string;
  roundNum: number;
  name: string;
  matches: BracketMatch[];
}

interface Bracket {
  id: string;
  name: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETE';
  rounds: BracketRound[];
}

interface Season {
  id: string;
  leagueId: string;
  startDate: string;
  endDate: string;
  status: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  PENDING: '#6b7280',
  IN_PROGRESS: '#f59e0b',
  COMPLETE: '#10b981',
};

// ─── Components ──────────────────────────────────────────────────────────────

function MatchCard({
  match,
  isAdmin,
  bracketId,
  seasonId,
  onUpdate,
}: {
  match: BracketMatch;
  isAdmin: boolean;
  bracketId: string;
  seasonId: string;
  onUpdate: () => void;
}) {
  const { slug } = useTenant();
  const { token } = useAuth();
  const [editing, setEditing] = useState(false);
  const [scoreA, setScoreA] = useState(match.scoreA ?? 0);
  const [scoreB, setScoreB] = useState(match.scoreB ?? 0);
  const [winnerId, setWinnerId] = useState(match.winnerId ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isBye = !match.teamAId || !match.teamBId;
  const isComplete = !!match.winnerId;

  const save = async () => {
    if (!token) return;
    setSaving(true); setErr(null);
    try {
      const res = await apiFetch(
        `/api/v1/seasons/${seasonId}/brackets/${bracketId}/matches/${match.id}`,
        {
          method: 'PATCH',
          slug,
          token,
          body: JSON.stringify({ scoreA, scoreB, winnerId: winnerId || undefined }),
        }
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.message || 'HTTP ' + res.status);
      }
      setEditing(false);
      onUpdate();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (isBye) {
    return (
      <div style={bracketMatchCardStyle}>
        <div style={bracketByeStyle}>BYE</div>
      </div>
    );
  }

  return (
    <div style={{ ...bracketMatchCardStyle, outline: isComplete ? '2px solid #10b981' : undefined }}>
      <div style={bracketTeamRowStyle}>
        <span style={{
          ...bracketTeamNameStyle,
          ...(match.winnerId === match.teamAId ? bracketWinnerStyle : {}),
        }}>
          {match.teamAName ?? 'TBD'}
        </span>
        <span style={bracketScoreStyle}>
          {match.scoreA !== null ? match.scoreA : '–'}
        </span>
      </div>
      <div style={bracketTeamRowStyle}>
        <span style={{
          ...bracketTeamNameStyle,
          ...(match.winnerId === match.teamBId ? bracketWinnerStyle : {}),
        }}>
          {match.teamBName ?? 'TBD'}
        </span>
        <span style={bracketScoreStyle}>
          {match.scoreB !== null ? match.scoreB : '–'}
        </span>
      </div>

      {isAdmin && !isComplete && !editing && (
        <button style={bracketSeedBtnStyle} onClick={() => setEditing(true)}>
          Enter result
        </button>
      )}
      {isAdmin && editing && (
        <div style={bracketSeedFormStyle}>
          <div style={bracketSeedRowStyle}>
            <span style={bracketSeedLabelStyle}>Score A</span>
            <input type="number" min={0} max={99} value={scoreA}
              onChange={(e) => setScoreA(Number(e.target.value))}
              style={bracketSeedInputStyle} />
          </div>
          <div style={bracketSeedRowStyle}>
            <span style={bracketSeedLabelStyle}>Score B</span>
            <input type="number" min={0} max={99} value={scoreB}
              onChange={(e) => setScoreB(Number(e.target.value))}
              style={bracketSeedInputStyle} />
          </div>
          <div style={bracketSeedRowStyle}>
            <span style={bracketSeedLabelStyle}>Winner</span>
            <select value={winnerId} onChange={(e) => setWinnerId(e.target.value)}
              style={bracketSeedSelectStyle}>
              <option value="">— pick winner —</option>
              {match.teamAId && <option value={match.teamAId}>{match.teamAName}</option>}
              {match.teamBId && <option value={match.teamBId}>{match.teamBName}</option>}
            </select>
          </div>
          {err && <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0' }}>{err}</p>}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button style={{ ...bracketSeedBtnStyle, background: '#10b981' }}
              onClick={save} disabled={saving || !winnerId}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button style={{ ...bracketSeedBtnStyle, background: '#6b7280' }}
              onClick={() => { setEditing(false); setErr(null); }} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {isComplete && (
        <div style={{ fontSize: 11, color: '#10b981', marginTop: 4, fontWeight: 600 }}>
          🏆 {match.winnerName}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PlayoffPage(): React.JSX.Element {
  const { slug } = useTenant();
  const { user, token } = useAuth();
  const isAdmin = !!user?.roles?.some((r: string) =>
    ['ORG_ADMIN', 'LEAGUE_ADMIN', 'SUPER_ADMIN'].includes(r)
  );

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>('');
  const [brackets, setBrackets] = useState<Bracket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed form state
  const [showSeedForm, setShowSeedForm] = useState(false);
  const [seedName, setSeedName] = useState('Playoffs');
  const [seedTeamCount, setSeedTeamCount] = useState(4);
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);

  // Load seasons on mount
  useEffect(() => {
    if (!slug) return;
    apiFetch('/api/v1/seasons', { slug, token })
      .then((r) => r.json())
      .then((data: Season[]) => {
        setSeasons(data);
        if (data.length > 0) setSelectedSeasonId(data[0].id);
      })
      .catch(() => {});
  }, [slug, token]);

  const loadBrackets = useCallback(async () => {
    if (!slug || !selectedSeasonId) return;
    setLoading(true); setError(null);
    try {
      const res = await apiFetch(
        `/api/v1/seasons/${selectedSeasonId}/brackets`,
        { slug, token }
      );
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const raw: Bracket[] = await res.json();
      // Fetch full bracket details (with team names) for each
      const full = await Promise.all(
        raw.map((b) =>
          apiFetch(`/api/v1/seasons/${selectedSeasonId}/brackets/${b.id}`, { slug, token })
            .then((r) => r.json())
        )
      );
      setBrackets(full);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load brackets');
    } finally {
      setLoading(false);
    }
  }, [slug, token, selectedSeasonId]);

  useEffect(() => { loadBrackets(); }, [loadBrackets]);

  const handleSeed = async () => {
    if (!token || !selectedSeasonId) return;
    setSeeding(true); setSeedError(null);
    try {
      const res = await apiFetch(
        `/api/v1/seasons/${selectedSeasonId}/brackets`,
        {
          method: 'POST',
          slug,
          token,
          body: JSON.stringify({ name: seedName, teamCount: seedTeamCount, seasonId: selectedSeasonId }),
        }
      );
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.message || 'HTTP ' + res.status);
      }
      setShowSeedForm(false);
      setSeedName('Playoffs');
      await loadBrackets();
    } catch (e: unknown) {
      setSeedError(e instanceof Error ? e.message : 'Failed to seed bracket');
    } finally {
      setSeeding(false);
    }
  };

  const formatDate = (iso: string) => {
    try { return new Date(iso).getFullYear().toString(); } catch { return ''; }
  };

  return (
    <main style={pageStyle}>
      <h1 style={headingStyle}>Playoff Brackets</h1>

      {/* Season selector */}
      <div style={{ marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 600, fontSize: 14 }}>Season:</label>
        <select
          value={selectedSeasonId}
          onChange={(e) => setSelectedSeasonId(e.target.value)}
          style={bracketSeedSelectStyle}
        >
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>
              {formatDate(s.startDate)} – {formatDate(s.endDate)} ({s.status})
            </option>
          ))}
        </select>

        {isAdmin && !showSeedForm && (
          <button style={bracketSeedBtnStyle} onClick={() => setShowSeedForm(true)}>
            + New Bracket
          </button>
        )}
      </div>

      {/* Seed form */}
      {isAdmin && showSeedForm && (
        <div style={{ ...bracketSeedFormStyle, marginBottom: 24, maxWidth: 360 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Seed New Bracket</h3>
          <div style={bracketSeedRowStyle}>
            <span style={bracketSeedLabelStyle}>Name</span>
            <input value={seedName} onChange={(e) => setSeedName(e.target.value)}
              style={bracketSeedInputStyle} placeholder="e.g. Spring Playoffs" />
          </div>
          <div style={bracketSeedRowStyle}>
            <span style={bracketSeedLabelStyle}>Teams</span>
            <select value={seedTeamCount} onChange={(e) => setSeedTeamCount(Number(e.target.value))}
              style={bracketSeedSelectStyle}>
              <option value={2}>2 teams</option>
              <option value={4}>4 teams</option>
              <option value={8}>8 teams</option>
              <option value={16}>16 teams</option>
            </select>
          </div>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 8px' }}>
            Teams are seeded automatically by win record from the regular season.
          </p>
          {seedError && <p style={{ color: '#ef4444', fontSize: 12 }}>{seedError}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...bracketSeedBtnStyle, background: '#E8507A' }}
              onClick={handleSeed} disabled={seeding || !seedName.trim()}>
              {seeding ? 'Seeding…' : 'Create Bracket'}
            </button>
            <button style={{ ...bracketSeedBtnStyle, background: '#6b7280' }}
              onClick={() => { setShowSeedForm(false); setSeedError(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <p style={mutedTextStyle}>Loading brackets…</p>}
      {error && (
        <div role="alert" style={errorBoxStyle}>
          {error}{' '}
          <button onClick={loadBrackets} style={retryBtnStyle}>Retry</button>
        </div>
      )}
      {!loading && !error && brackets.length === 0 && (
        <p style={mutedTextStyle}>
          No playoff brackets yet for this season.
          {isAdmin && ' Use "+ New Bracket" to seed one.'}
        </p>
      )}

      {brackets.map((bracket) => (
        <div key={bracket.id} style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <h2 style={{ ...headingStyle, margin: 0, fontSize: '1.1rem' }}>{bracket.name}</h2>
            <span style={bracketStatusBadgeStyle(STATUS_COLOR[bracket.status])}>
              {bracket.status.replace('_', ' ')}
            </span>
          </div>

          {/* Bracket grid: one column per round */}
          <div style={bracketWrapStyle}>
            {bracket.rounds.map((round) => (
              <div key={round.id} style={bracketRoundColStyle}>
                <div style={bracketRoundTitleStyle}>{round.name}</div>
                {round.matches.map((match) => (
                  <MatchCard
                    key={match.id}
                    match={match}
                    isAdmin={isAdmin}
                    bracketId={bracket.id}
                    seasonId={selectedSeasonId}
                    onUpdate={loadBrackets}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </main>
  );
}
